export type TryResult<T> = { status: 'success'; value: T } | { status: 'failure'; err: unknown }

export function tryResult<T>(action: () => Promise<T>): Promise<TryResult<T>>
export function tryResult<T>(action: () => T): TryResult<T>
export function tryResult<T>(action: () => T | Promise<T>): TryResult<T> | Promise<TryResult<T>> {
  try {
    const value = action()
    if (value instanceof Promise) {
      return value.then(
        result => ({ status: 'success', value: result }),
        err => ({ status: 'failure', err }),
      )
    }
    return { status: 'success', value }
  } catch (err) {
    return { status: 'failure', err }
  }
}

export function tryOrDefault<T>(fn: () => T): T | undefined
export function tryOrDefault<T>(fn: () => T, fallback: T): T
export function tryOrDefault<T>(fn: () => Promise<T>): Promise<T | undefined>
export function tryOrDefault<T>(fn: () => Promise<T>, fallback: T): Promise<T>
export function tryOrDefault<T>(
  fn: () => T | Promise<T>,
  fallback?: T,
): T | Promise<T> | undefined | Promise<T | undefined> {
  try {
    const result = fn()
    if (result && typeof (result as Promise<T>).then === 'function') {
      return (result as Promise<T>).catch(() => fallback)
    }
    return result
  } catch {
    return fallback
  }
}

type TryUntilOptions = {
  intervalMs: number
  signal?: AbortSignal
  onError?: (err: unknown) => void
}

const waitUntilNextAttempt = (intervalMs: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason)
      return
    }

    const complete = () => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }
    const abort = () => {
      clearTimeout(timeout)
      reject(signal?.reason)
    }
    const timeout = setTimeout(complete, intervalMs)
    signal?.addEventListener('abort', abort, { once: true })
  })

export const tryUntil = async <T>(
  action: () => Promise<T | false | null | undefined>,
  options: TryUntilOptions,
): Promise<T> => {
  while (true) {
    options.signal?.throwIfAborted()
    try {
      const result = await action()
      options.signal?.throwIfAborted()
      if (result) return result
    } catch (err) {
      options.signal?.throwIfAborted()
      options.onError?.(err)
    }
    await waitUntilNextAttempt(options.intervalMs, options.signal)
  }
}

export interface FormatErrorOptions {
  includeStack?: boolean
  maxLength?: number
}

/**
 * Formats an unknown error value into a user-friendly string.
 * - If the value is an Error, includes name, message, and optionally the full stack.
 * - Traverses `cause` chain (if present) and appends it as nested sections.
 * - If `maxLength` is provided, trims the output to that length. When traversing nested
 *   causes, if appending the next part would exceed `maxLength`, aborts early and returns
 *   the truncated string.
 */
export function formatError(err: unknown, options?: FormatErrorOptions): string {
  const includeStack = options?.includeStack ?? true
  const maxLength = options?.maxLength

  const parts: string[] = []

  const pushPart = (text: string) => {
    if (text.length === 0) return true
    if (maxLength === undefined) {
      parts.push(text)
      return true
    }
    // Check if adding this text would exceed max
    const currentLen = parts.join('').length
    const remaining = maxLength - currentLen
    if (remaining <= 0) return false
    if (text.length <= remaining) {
      parts.push(text)
      return true
    }
    // Trim and abort early
    const trimmed = `${text.slice(0, Math.max(0, remaining - 1))}…`
    parts.push(trimmed)
    return false
  }

  const seen = new Set<unknown>()

  const renderUnknown = (value: unknown) => {
    try {
      if (typeof value === 'string') return value
      if (typeof value === 'number' || typeof value === 'boolean' || value == null) return String(value)
      if (typeof value === 'bigint') return value.toString()
      if (typeof value === 'symbol') return value.toString()
      return JSON.stringify(value)
    } catch {
      return Object.prototype.toString.call(value)
    }
  }

  const walk = (value: unknown, depth: number) => {
    if (!pushPart(depth === 0 ? '' : `\nCaused by: `)) return false

    if (value instanceof Error) {
      const name = value.name || 'Error'
      const message = value.message || String(value)
      if (!pushPart(`${name}: ${message}`)) return false

      // Walk causes if any (Error.cause is standard; could be any unknown)
      const cause = (value as unknown as { cause?: unknown }).cause
      if (cause && !seen.has(cause)) {
        seen.add(cause)
        return walk(cause, depth + 1)
      }

      // Append stack AFTER traversing causes so that causes remain visible even with maxLength
      if (includeStack) {
        // Prefer stack if present; include it fully as-is
        const stack = typeof value.stack === 'string' ? value.stack : undefined
        if (stack) {
          // If the stack already contains name/message, avoid duplicating the first line
          // but keep it simple: include as-is after a newline
          if (!pushPart(`\n${stack}`)) return false
        }
      }
      return true
    } else {
      // Non-Error values
      return pushPart(renderUnknown(value))
    }
  }

  seen.add(err)
  walk(err, 0)

  let out = parts.join('')
  if (maxLength !== undefined && out.length > maxLength) {
    out = `${out.slice(0, Math.max(0, maxLength - 1))}…`
  }
  return out
}
